import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import Heading from "../../../../../../common/element/Heading/index.js";
import Dropdown from "../../../../../../common/element/Dropdown/Dropdown.js";
import { setBreadcrumbs } from "../../../../../../redux/classes/breadcrumbsSlice.js";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Pagination } from "../../../../../../common/pagination/index.js";
import useUnauthorizedHandler from "../../../../../../common/snippet/useUnauthorizedHandler.js";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader/index.js";
import ThreeDotLoader from "../../../../../../common/element/ButtonLoader/ThreeDotLoader";
import moment from "moment";
import Input from "../../../../../../common/element/Input/Input.js";
import logoPDF from "../ProvidersOverview/LogoPDF.png";
import DataList from "./DataList.js";
import ClientMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/ClientMultiSelectDropdown.js";
import StatusMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/StatusMultiSelectDropdown.js";
import AllProviderMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/AllProviderMultiSelectDropdown.js";
import ProvideTyperMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/ProvideTyperMultiSelectDropdown.js";
import AllStatesMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/AllStatesMultiSelectDropdown.js";
import ServicesMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/ServicesMultiSelectDropdown.js";
import DocumentTypesMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/DocumentTypesMultiSelectDropdown.js";
import DateMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/DateMultiSelectDropdown.js";
import { GenerateExpiryReport } from "../../../../../../actions/Admin/reports/GenerateExpiryReport.js";

const ExpiryReport = () => {
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const dispatch = useDispatch();

  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataList, setDataList] = useState({ DocsReport: [], total: 0 });

  const [filters, setFilters] = useState({
    client_org_ids: [],
    provider_org_ids: [],
    provider_type_ids: [],
    provider_statuses: [],
    states: [],
    services: [],
    contact_name: "",
    document_type_ids: [],
    expiry_date_range: "",
    search: "",
    page: 1,
    limit: 10,
    sort: "expiry_date",
    order: "asc",
  });

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handleSort = (e, field) => {
    setFilters((prev) => {
      const isAsc = prev.sort === field && prev.order === "asc";
      return {
        ...prev,
        sort: field,
        order: isAsc ? "desc" : "asc",
        page: 1,
      };
    });
  };

  const fetchReport = useCallback(
    async (paramsPayload) => {
      setLoading(true);
      try {
        const res = await dispatch(GenerateExpiryReport(paramsPayload));
        if (res?.success === true) {
          setDataList({
            DocsReport: res.data?.DocsReport || [],
            total: res.data?.total || 0,
          });
        } else if (res?.status === 401) {
          handleUnauthorizedAccess();
        } else {
          setDataList({ DocsReport: [], total: 0 });
        }
      } catch (error) {
        console.error("Error fetching expiry report:", error);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, handleUnauthorizedAccess]
  );

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          label: "Reports",
          url: "/admin/reports",
          home: "/admin/dashboard",
        },
        {
          label: "Expiry Report",
          url: "/admin/reports/expiry-report",
        },
      ])
    );
  }, [dispatch]);

  // Trigger report fetch when any of these parameters change
  useEffect(() => {
    fetchReport(filters);
  }, [
    filters.client_org_ids,
    filters.provider_org_ids,
    filters.provider_type_ids,
    filters.provider_statuses,
    filters.states,
    filters.services,
    filters.contact_name,
    filters.document_type_ids,
    filters.expiry_date_range,
    filters.search,
    filters.page,
    filters.limit,
    filters.sort,
    filters.order,
    fetchReport,
  ]);

  const handleGenerate = () => {
    setIsGenerating(true);
    fetchReport(filters).finally(() => setIsGenerating(false));
  };

  // Base64 helper for PDF export logo
  function getBase64ImageFromUrl(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute("crossOrigin", "anonymous");
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      };
      img.onerror = (error) => reject(error);
      img.src = url;
    });
  }

  const handleExport = async (format) => {
    // Fetch all records for export (without pagination limit)
    const exportParams = {
      ...filters,
      page: 1,
      limit: 10000, // Safe large limit for export
    };

    try {
      const res = await dispatch(GenerateExpiryReport(exportParams));
      const records = res?.data?.DocsReport || [];
      if (!records.length) {
        alert("No data available to export");
        return;
      }

      const headers = [
        "Client Name",
        "Provider ID",
        "Provider Name",
        "Provider Status",
        "State",
        "Contact Name",
        "Document Type",
        "Document Name",
        "Expiry Date",
      ];

      if (format === "csv" || format === "excel") {
        const aoa = [headers];
        records.forEach((r) => {
          aoa.push([
            r.client_trading_name || "-",
            r.provider_org_id || "-",
            r.provider_trading_name || "-",
            r.provider_status || "-",
            r.state_name || "-",
            r.contact_name || "-",
            r.document_type_name || "-",
            r.document_name || "-",
            r.expiry_date ? moment(r.expiry_date).format("DD/MM/YYYY") : "-",
          ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Expiry Report");

        if (format === "csv") {
          XLSX.writeFile(wb, "expiry_report.csv", { bookType: "csv" });
        } else {
          XLSX.writeFile(wb, "expiry_report.xlsx", { cellStyles: true });
        }
      } else if (format === "pdf") {
        const doc = new jsPDF("l"); // landscape mode
        const today = moment().format("DD/MM/YYYY");
        
        doc.setFontSize(12);
        doc.text(today, 14, 15);
        
        const title = "Document Expiry Report";
        doc.setFontSize(16);
        const pageWidth = doc.internal.pageSize.getWidth();
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, 15);

        // Add logo if available
        let logoData;
        try {
          logoData = await getBase64ImageFromUrl(logoPDF);
        } catch (error) {
          console.error("Error converting image to Base64", error);
        }

        if (logoData) {
          const maxWidth = 45;
          const maxHeight = 15;
          const marginRight = 15;
          const imgProps = doc.getImageProperties(logoData);
          const ratio = imgProps.width / imgProps.height;
          let finalWidth = maxWidth;
          let finalHeight = finalWidth / ratio;
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = finalHeight * ratio;
          }
          doc.addImage(logoData, "PNG", pageWidth - finalWidth - marginRight, 8, finalWidth, finalHeight);
        }

        const tableRows = records.map((r) => [
          r.client_trading_name || "-",
          r.provider_org_id || "-",
          r.provider_trading_name || "-",
          r.provider_status || "-",
          r.state_name || "-",
          r.contact_name || "-",
          r.document_type_name || "-",
          r.document_name || "-",
          r.expiry_date ? moment(r.expiry_date).format("DD/MM/YYYY") : "-",
        ]);

        doc.autoTable({
          head: [headers],
          body: tableRows,
          startY: 28,
          theme: "grid",
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [87, 134, 204], textColor: 255 },
        });

        doc.save("expiry_report.pdf");
      }
    } catch (error) {
      console.error("Error exporting report data:", error);
    }
  };

  return (
    <React.Fragment>
      <div className="flex justify-between mb-5 border-b border-gray-200 pb-5">
        <Heading>Expiry Report</Heading>
        <div className="flex gap-3">
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="bg-[#5786CC] text-white text-xs px-3 py-2 rounded-md hover:bg-[#3f6ab4] transition flex items-center justify-center min-w-[90px] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGenerating ? <ThreeDotLoader /> : "Generate"}
          </button>

          <div className="export-dropdown">
            <Dropdown title="Export" className="dropdown-btn">
              <Dropdown.Item
                eventKey="csv"
                onClick={() => handleExport("csv")}
                className="text-gray-900 font-medium"
              >
                CSV
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="excel"
                onClick={() => handleExport("excel")}
                className="text-gray-900 font-medium"
              >
                Excel
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="pdf"
                onClick={() => handleExport("pdf")}
                className="text-gray-900 font-medium"
              >
                PDF
              </Dropdown.Item>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Filter Row 1 */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
        <ClientMultiSelectDropdown
          title="Clients"
          updateFilter={updateFilter}
        />
        <AllProviderMultiSelectDropdown
          title="Providers"
          updateFilter={updateFilter}
          client_org_ids={filters.client_org_ids}
        />
        <ProvideTyperMultiSelectDropdown
          title="Provider Types"
          updateFilter={updateFilter}
          provider_org_ids={filters.provider_org_ids}
        />
        <StatusMultiSelectDropdown
          title="Status"
          updateFilter={updateFilter}
          fieldName="provider_statuses"
          multiSelect={true}
          statusData={[
            "Compliant",
            "Non-compliant",
            "Invited",
            { label: "Self-Registered", value: "Registered" },
            { label: "Registered", value: "Assigned" },
            { label: "Deactivated", value: "Closed" },
          ]}
        />
        <AllStatesMultiSelectDropdown
          title="States"
          updateFilter={updateFilter}
        />
        <ServicesMultiSelectDropdown
          title="Services"
          updateFilter={updateFilter}
        />
        <DocumentTypesMultiSelectDropdown
          title="Doc Types"
          updateFilter={updateFilter}
        />
        <DateMultiSelectDropdown
          title="Expiry Date"
          updateFilter={updateFilter}
          type="expiry_date_range"
        />
      </div>

      {/* Filter Row 2: Search Inputs */}
      <div className="flex flex-wrap gap-4 mb-5 items-center">
        <div className="w-72">
          <Input
            placeholder="Search by client contact name"
            value={filters.contact_name}
            onChange={(e) => updateFilter("contact_name", e.target.value)}
            className="w-full"
          />
        </div>
        <div className="w-72">
          <Input
            placeholder="Search by document type / name"
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="w-full"
          />
        </div>
        <button
          onClick={() =>
            setFilters({
              client_org_ids: [],
              provider_org_ids: [],
              provider_type_ids: [],
              provider_statuses: [],
              states: [],
              services: [],
              contact_name: "",
              document_type_ids: [],
              expiry_date_range: "",
              search: "",
              page: 1,
              limit: 10,
              sort: "expiry_date",
              order: "asc",
            })
          }
          className="text-xs text-gray-500 hover:text-gray-800 transition"
        >
          Reset Filters
        </button>
      </div>

      {/* Table Data Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <ThreeDotBouncing />
        </div>
      ) : (
        <React.Fragment>
          <DataList
            data={dataList}
            handleSort={handleSort}
            currentFilters={filters}
          />
          <div className="flex justify-between items-center mt-5">
            <span className="text-sm text-gray-500">
              Showing {dataList.DocsReport.length} of {dataList.total} entries
            </span>
            <Pagination
              currentPage={filters.page}
              totalItems={dataList.total}
              itemsPerPage={filters.limit}
              onPageChange={handlePageChange}
            />
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default ExpiryReport;
